/**
 * API functions for managing class collaborators
 */

import { api } from "@/lib/api/api-client";

/**
 * Collaborator profile info
 */
export interface CollaboratorProfile {
  id: string;
  username: string | null;
  full_name: string | null;
  profile_photo: string | null;
}

/**
 * Profile search result
 */
export interface ProfileSearchResult extends CollaboratorProfile {
  /** @deprecated This field is not reliably maintained and should not be used */
  is_instructor?: boolean;
}

/**
 * Class info for pending invitation
 */
export interface InvitationClassInfo {
  id: string;
  title: string;
  location: string;
  date: string;
  start_time: string;
  end_time: string;
  timezone: string;
}

/**
 * Pending invitation data
 */
export interface PendingInvitation {
  id: string;
  status: "pending";
  created_at: string;
  class: InvitationClassInfo;
  inviter: CollaboratorProfile;
}

/**
 * Collaborator with full details
 */
export interface ClassCollaborator {
  id: string;
  class_id: string;
  inviter_profile_id: string;
  invitee_profile_id: string;
  status: "pending" | "accepted" | "declined";
  created_at: string;
  responded_at: string | null;
  profile: CollaboratorProfile;
}

// =============================================================================
// PROFILE SEARCH
// =============================================================================

interface SearchProfilesResponse {
  profiles: ProfileSearchResult[];
  total: number;
}

/**
 * Search profiles by username or name
 * @param query Search query string
 * @param excludeIds Profile IDs to exclude from results
 * @param includeSelf Whether to include the current user in results (default: false)
 * @returns Array of matching profiles
 */
export async function searchProfiles(
  query: string,
  excludeIds: string[] = [],
  includeSelf: boolean = false
): Promise<ProfileSearchResult[]> {
  const params = new URLSearchParams({ q: query });
  if (excludeIds.length > 0) {
    params.append("exclude_ids", excludeIds.join(","));
  }
  if (includeSelf) {
    params.append("include_self", "true");
  }

  const response = await api.get<SearchProfilesResponse>(
    `/profiles/search?${params.toString()}`
  );
  return response.profiles;
}

// =============================================================================
// PENDING INVITATIONS
// =============================================================================

interface GetPendingInvitationsResponse {
  invitations: PendingInvitation[];
  total: number;
}

/**
 * Fetch all pending collaboration invitations for the current user
 * @returns Array of pending invitations
 */
export async function fetchPendingInvitations(): Promise<PendingInvitation[]> {
  const response = await api.get<GetPendingInvitationsResponse>(
    "/collaborations/pending"
  );
  return response.invitations;
}

interface RespondToInvitationResponse {
  success: boolean;
  status: "accepted" | "declined";
}

/**
 * Accept or decline a collaboration invitation
 * @param invitationId The invitation ID
 * @param accept True to accept, false to decline
 */
export async function respondToInvitation(
  invitationId: string,
  accept: boolean
): Promise<void> {
  await api.put<RespondToInvitationResponse>(
    `/collaborations/${invitationId}/respond`,
    { accept }
  );
}

// =============================================================================
// CLASS COLLABORATORS
// =============================================================================

interface GetCollaboratorsResponse {
  collaborators: ClassCollaborator[];
  total: number;
}

/**
 * Get accepted collaborators for a class
 * @param classId The class ID
 * @returns Array of collaborators
 */
export async function getClassCollaborators(
  classId: string
): Promise<CollaboratorProfile[]> {
  const response = await api.get<GetCollaboratorsResponse>(
    `/classes/${classId}/collaborators`,
    { skipAuth: true }
  );
  return response.collaborators.map((c) => c.profile);
}

/**
 * Get full collaborator records for a class (includes record IDs needed for removal)
 * @param classId The class ID
 * @param includePending Whether to include pending invitations (requires auth + owner)
 * @returns Array of full collaborator records
 */
export async function getClassCollaboratorRecords(
  classId: string,
  includePending = false
): Promise<ClassCollaborator[]> {
  const params = includePending ? '?include_pending=true' : '';
  const response = await api.get<GetCollaboratorsResponse>(
    `/classes/${classId}/collaborators${params}`,
    includePending ? undefined : { skipAuth: true }
  );
  return response.collaborators;
}

interface InviteCollaboratorResponse {
  success: boolean;
  collaborator: ClassCollaborator;
}

/**
 * Invite a collaborator to a class
 * @param classId The class ID
 * @param profileId The profile ID of the user to invite
 */
export async function inviteCollaborator(
  classId: string,
  profileId: string
): Promise<void> {
  await api.post<InviteCollaboratorResponse>(
    `/classes/${classId}/collaborators`,
    { invitee_profile_id: profileId }
  );
}

interface RemoveCollaboratorResponse {
  success: boolean;
  message: string;
}

/**
 * Remove a collaborator from a class
 * @param classId The class ID
 * @param collaboratorId The collaborator record ID
 */
export async function removeCollaborator(
  classId: string,
  collaboratorId: string
): Promise<void> {
  await api.delete<RemoveCollaboratorResponse>(
    `/classes/${classId}/collaborators/${collaboratorId}`
  );
}
